import React, { useCallback } from 'react'
import { useTranslation, Trans, TransProps } from 'react-i18next'
import { TOptions } from 'i18next'
import { TranslationKey } from './types'

// Type for the translation function
export type TranslateFunction = <K extends TranslationKey>(
    key: K,
    options?: TOptions
) => string

/**
 * Type-safe Trans component props
 * Constraints i18nKey to valid TranslationKey values
 */
export interface TypedTransProps extends Omit<TransProps<string>, 'i18nKey'> {
    i18nKey?: TranslationKey | TranslationKey[]
}

/**
 * Type-safe translation hook
 * Provides compile-time type checking for translation keys
 * 
 * @example
 * ```tsx
 * const { t } = useTar();
 * 
 * // Type-safe usage
 * const welcomeText = t('common.welcome'); // ✅ Valid
 * const invalidText = t('invalid.key'); // ❌ TypeScript error
 * ```
 */
export function useTar() {
    const { t, i18n } = useTranslation()

    /**
     * Type-safe translation function
     * @param key - Translation key in dot notation (e.g., 'common.welcome')
     * @param options - Optional interpolation options
     * @returns Translated string
     */
    const translate = useCallback(
        <K extends TranslationKey>(key: K, options?: TOptions): string => {
            return t(key, options) as string
        },
        [t]
    )

    /**
     * Change the current language
     * @param lng - Language code (e.g., 'en', 'es', 'fr')
     */
    const changeLanguage = (lng: string) => {
        return i18n.changeLanguage(lng)
    }

    /**
     * Get the current language
     */
    const getCurrentLanguage = () => {
        return i18n.language
    }

    /**
     * Check if a language is available
     * @param lng - Language code to check
     */
    const hasLanguage = (lng: string) => {
        return i18n.hasResourceBundle(lng, 'translation')
    }

    return {
        t: translate,
        changeLanguage,
        getCurrentLanguage,
        hasLanguage,
        i18n,
    }
}

/**
 * Type-safe Trans component
 * Provides compile-time type checking for translation keys with rich text formatting
 * 
 * @example
 * ```tsx
 * import { TypedTrans } from '@modules/i18n';
 * 
 * // Type-safe usage with components
 * <TypedTrans
 *   i18nKey="common.iAgreeToTheXandY"
 *   components={[
 *     <Link key="terms" to="/terms" />,
 *     <Link key="privacy" to="/privacy" />
 *   ]}
 * />
 * ```
 */
export const TypedTrans: React.FC<TypedTransProps> = (props) => {
    return <Trans {...props} />
}

export default useTar

